import { defineComponent, ref } from 'vue';
import {
  getDictionaryValueList,
  delDictionaryValue,
  updateDictionaryValue,
  updateDictionaryValueStatus,
} from '@/api/system-config/index';
import EditValueDialog from './edit-value-dialog';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Edit, Plus, Delete } from '@element-plus/icons-vue';
import styles from './style.module.scss';
import { ITreeDataItem } from './type.d';
import {
  DICTIONARY_STATUS_ENUM,
  DICTIONARY_STATUS_LIST,
  DICTIONARY_CHANNEL_ENUM,
} from '@/constant';

export default defineComponent({
  components: {
    EditValueDialog,
  },
  emits: ['update:visible'],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    dictId: {
      type: String,
      default: '',
    },
    dictCode: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const treeData = ref<ITreeDataItem[]>([]);
    const expandedKeys = ref<string[]>([]);
    const getData = async () => {
      try {
        const {
          data: { list },
        } = await getDictionaryValueList({
          dictCode: props.dictCode,
          pageNum: 1,
          pageSize: 10000,
        });
        const result: ITreeDataItem[] = [];
        const itemMap: {
          [key: string]: ITreeDataItem;
        } = {};
        list.forEach((item) => {
          const code = item.valueCode;
          const pcode = item.valueParentCode;
          if (!itemMap[code]) {
            itemMap[code] = {
              valueParentId: '',
              dictCode: '',
              channel: DICTIONARY_CHANNEL_ENUM.BACKGROUND,
              isEnable: DICTIONARY_STATUS_ENUM.ENABLE,
              valueParentCode: '',
              valueCode: '',
              remark: '',
              sort: '',
              dictValueId: '',
              value: '',
              children: [],
            };
          }
          itemMap[code] = {
            ...item,
            valueParentId: '',
            children: itemMap[code].children.map(v => ({
              ...v,
              valueParentId: item.dictValueId,
            })),
          };
          const treeItem = itemMap[code];
          if (pcode === '') {
            result.push(treeItem);
          } else {
            // treeItem.valueParentId = pid;
            if (!itemMap[pcode]) {
              itemMap[pcode] = {
                valueParentId: '',
                dictCode: '',
                channel: DICTIONARY_CHANNEL_ENUM.BACKGROUND,
                isEnable: DICTIONARY_STATUS_ENUM.ENABLE,
                valueParentCode: '',
                valueCode: '',
                remark: '',
                sort: '',
                dictValueId: '',
                value: '',
                children: [],
              };
            }
            itemMap[pcode]?.children?.push({
              ...treeItem,
              valueParentId: itemMap[pcode].dictValueId,
            });
          }
        });
        treeData.value = result;
      } catch (error) {
        console.log(error);
      }
    };
    const editFormConfig = ref({
      visible: false,
      formData: {},
    });
    const handleCloseDialog = () => {
      expandedKeys.value = [];
      emit('update:visible', false);
    };
    const handleOpenDialog = () => {
      getData();
    };
    // 编辑
    const handleEditItem = (row: ITreeDataItem, e: MouseEvent) => {
      e.stopPropagation();
      editFormConfig.value.formData = {
        ...row,
        dictCode: props.dictCode,
      };
      editFormConfig.value.visible = true;
    };
    // 新增
    const handleAdd = (valueParentId: string = '', valueParentCode: string = '', e: MouseEvent) => {
      e.stopPropagation();
      editFormConfig.value.formData = {
        dictCode: props.dictCode,
        valueParentId,
        valueParentCode,
      };
      editFormConfig.value.visible = true;
    };
    // 删除
    const handleDelItem = async (dictValueId: string, e: MouseEvent) => {
      try {
        e.stopPropagation();
        await ElMessageBox.confirm('请确认是否删除', '提示');
        await delDictionaryValue({
          dictValueId,
        });
        ElMessage.success('删除成功');
        getData();
      } catch (error) {
        console.log(error);
      }
    };
    // 更新状态
    const handleUpdateStatus = async (row: ITreeDataItem, e: MouseEvent) => {
      try {
        e.stopPropagation();
        await updateDictionaryValueStatus({
          id: row.dictValueId,
          isEnable: row.isEnable === DICTIONARY_STATUS_ENUM.ENABLE
            ? DICTIONARY_STATUS_ENUM.DISENABLE : DICTIONARY_STATUS_ENUM.ENABLE,
        });
        ElMessage.success('操作成功');
        getData();
      } catch (error) {
        console.log(error);
      }
    };
    // 节点拖拽移动
    const handleNodeMove = async (
      currentNode: any,
      insertNode: any,
      position: 'before' | 'after' | 'inner',
    ) => {
      try {
        let sort = 0;
        let valueParentId = '';
        let valueParentCode = '';
        const insertRow: ITreeDataItem = insertNode.data;
        if (position === 'inner') {
          valueParentId = insertRow.dictValueId;
          valueParentCode = insertRow.valueCode;
          if (insertRow.children.length) {
            sort = Number(insertRow.children[insertRow.children.length - 1].sort) - 1;
          }
        }
        if (position === 'before') {
          sort = Number(insertRow.sort) + 1;
          valueParentId = insertRow.valueParentId;
          valueParentCode = insertRow.valueParentCode;
        }
        if (position === 'after') {
          sort = Number(insertRow.sort) - 1;
          valueParentId = insertRow.valueParentId;
          valueParentCode = insertRow.valueParentCode;
        }
        if (sort < 0) sort = 0;
        await updateDictionaryValue({
          ...currentNode.data,
          valueParentId,
          valueParentCode,
          sort,
        });
        getData();
      } catch (error) {
        console.log('更新节点排序失败', error);
      }
    };
    const handleExpandNode = (row: ITreeDataItem) => {
      expandedKeys.value.push(row.dictValueId);
    };
    const handleCloseNode = (row: ITreeDataItem) => {
      const index = expandedKeys.value.findIndex(v => v === row.dictValueId);
      if (index !== -1) {
        expandedKeys.value.splice(index, 1);
      }
    };

    return {
      handleExpandNode,
      handleCloseNode,
      expandedKeys,
      handleUpdateStatus,
      handleNodeMove,
      treeData,
      getData,
      handleAdd,
      editFormConfig,
      handleEditItem,
      handleDelItem,
      handleOpenDialog,
      handleCloseDialog,
    };
  },
  render() {
    return (
      <div>
        <el-dialog
          modelValue={this.visible}
          onClose={this.handleCloseDialog}
          onOpen={this.handleOpenDialog}
          center
          width="800px"
          close-on-press-escape={false}
          close-on-click-modal={false}
          custom-class="dialog-width-medium el-dialog-inner-scroll"
        >
          <el-row type="flex" style={{ paddingBottom: '5px' }}>
            <el-button size="default" type="primary" onClick={(e: MouseEvent) => this.handleAdd('', '', e)}>
              新增
            </el-button>
          </el-row>
          <div class={`${styles.treeContainer} treeContainer`}>
            <el-tree
              default-expanded-keys={this.expandedKeys}
              data={this.treeData}
              props={{
                children: 'children',
                label: 'value',
              }}
              node-key="dictValueId"
              onNodeDrop={this.handleNodeMove}
              onNodeExpand={this.handleExpandNode}
              onNodeCollapse={this.handleCloseNode}
              draggable
              v-slots={{
                default: ({ data }: { data: ITreeDataItem; }) => {
                  return (
                    <div class={styles.treeItem}>
                      <div class={styles.label}>
                        {data.value}
                        （编码：{data.valueCode} - {this.$filters.getEnumLabel(DICTIONARY_STATUS_LIST, data.isEnable)})
                      </div>
                      <div class={styles.btns}>
                        <el-button
                          type="text"
                          icon={Plus}
                          onClick={(e: MouseEvent) => this.handleAdd(data.dictValueId, data.valueCode, e)}
                        />
                        <el-button
                          type="text"
                          icon={Edit}
                          onClick={(e: MouseEvent) => this.handleEditItem(data, e)}
                        />
                        <el-button
                          type="text"
                          icon={Delete}
                          onClick={(e: MouseEvent) => this.handleDelItem(data.dictValueId, e)}
                        />
                        {data.isEnable === DICTIONARY_STATUS_ENUM.DISENABLE ? (
                          <el-button
                            type="text"
                            onClick={(e: MouseEvent) => this.handleUpdateStatus(data, e)}
                          >启用</el-button>
                        ) : (
                          <el-button
                            type="text"
                            style={{ color: 'red' }}
                            onClick={(e: MouseEvent) => this.handleUpdateStatus(data, e)}
                          >禁用</el-button>
                        )}
                      </div>
                    </div>
                  );
                },
              }}
            />
          </div>
        </el-dialog>
        <edit-value-dialog
          v-model={[this.editFormConfig.visible, 'visible']}
          formData={this.editFormConfig.formData}
          onSuccess={() => this.getData()}
        />
      </div>
    );
  },
});
